import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpContextToken,
    HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, from, Observable, retry, throwError } from "rxjs";
import { CommonService } from "app/services/common/common.service";
import { SnackbarTypes } from "@models/snackbar-model";

export const BYPASS_AUTH_ERROR_HANDLING = new HttpContextToken<boolean>(() => false);

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const httpInterceptor = (
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
    const _commonService = inject(CommonService);

    function blobToJsonUsingFileReader(blob: Blob): Promise<any> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(JSON.parse(reader.result as string));
            reader.onerror = () => reject(reader.error);
            reader.readAsText(blob); // Lire en tant que texte
        });
    }


    if (request.context.get(BYPASS_AUTH_ERROR_HANDLING)) {
        return next(request);
    }

    return next(request).pipe(
        retry(0),
        catchError((error: HttpErrorResponse) => {
            switch (error.status) {
                case 400: {
                    if (error.error.errorKey != 'E704' && error.error.errorKey != 'E764' && error.error.errorKey != 'E766') {
                        const handleErrorMessage = (errorObj: any) => {
                            if (errorObj?.errorKey) {
                                return `errors.${errorObj.errorKey}`;
                            }
                            if (errorObj?.message?.startsWith('error.E')) {
                                return `errors.${errorObj.message.split('error.')[1]?.trim()}`;
                            }
                            return 'errors.standardErrorMessage400';
                        };

                        if (error.error?.errorKey || error.error?.message) {
                            showErrorSnackbar({
                                errorMessage: handleErrorMessage(error.error),
                                commonService: _commonService,
                            });
                        } else if (error.error instanceof Blob && error.error.type === 'application/problem+json') {
                            from(blobToJsonUsingFileReader(error.error))
                                .pipe(
                                    catchError(() => throwError(() => 'errors.standardErrorMessage400'))
                                )
                                .subscribe(jsonError => {
                                    showErrorSnackbar({
                                        errorMessage: handleErrorMessage(jsonError),
                                        commonService: _commonService,
                                    });
                                });
                        } else {
                            showErrorSnackbar({
                                errorMessage: 'errors.standardErrorMessage400',
                                commonService: _commonService,
                            });
                        }
                    }
                    break;
                }

                case 401: {
                    showErrorSnackbar({
                        errorMessage: 'errors.standardErrorMessage401',
                        commonService: _commonService,
                    });
                    _commonService.logoutImmediately();
                    break;
                }

                case 403: {
                    // if (!(request.url.split('/').includes('public'))) {
                    // }
                    if (error.error && error.error.errorKey) {
                        showErrorSnackbar({
                            errorMessage: "errors." + error.error.errorKey,
                            commonService: _commonService,
                        });
                    } else {
                        showErrorSnackbar({
                            errorMessage: "errors.standardErrorMessage403",
                            commonService: _commonService,
                        });
                    }
                    break;
                }

                case 404: {
                    if (!request.url.includes("/account-management/api/users/email/")) {
                        showErrorSnackbar({
                            errorMessage: "errors.standardErrorMessage404",
                            commonService: _commonService,
                        });
                    }
                    break;
                }

                case 405: {
                    if (error.error && error.error.errorKey) {
                        showErrorSnackbar({
                            errorMessage: "errors." + error.error.errorKey,
                            commonService: _commonService,
                        });
                    } else {
                        showErrorSnackbar({
                            errorMessage: "errors.standardErrorMessage405",
                            commonService: _commonService,
                        });
                    }
                    break;
                }

                case 500: {
                    if (error.error && error.error.errorKey) {
                        showErrorSnackbar({
                            errorMessage: "errors." + error.error.errorKey,
                            commonService: _commonService,
                        });
                    } else {
                        showErrorSnackbar({
                            errorMessage: "errors.standardErrorMessage500",
                            commonService: _commonService,
                        });
                    }
                    break;
                }

                case 502: {
                    if (error.error && error.error.errorKey) {
                        showErrorSnackbar({
                            errorMessage: "errors." + error.error.errorKey,
                            commonService: _commonService,
                        });
                    } else {
                        showErrorSnackbar({
                            errorMessage: "errors.standardErrorMessage502",
                            commonService: _commonService,
                        });
                    }
                    break;
                }

                case 504: {
                    if (error.error && error.error.errorKey) {
                        showErrorSnackbar({
                            errorMessage: "errors." + error.error.errorKey,
                            commonService: _commonService,
                        });
                    } else {
                        showErrorSnackbar({
                            errorMessage: "errors.standardErrorMessage504",
                            commonService: _commonService,
                        });
                    }
                    break;
                }

                default: {
                    showErrorSnackbar({
                        errorMessage: "errors.unknown_error",
                        commonService: _commonService,
                    });
                    break;
                }
            }
            return throwError(error);
        }),
    );
};

const showErrorSnackbar = ({
    errorMessage,
    commonService,
}: {
    errorMessage: string;
    commonService?: CommonService;
}) => {
    commonService?.showSnackBar(
        errorMessage,
        SnackbarTypes.errorMessage,
    );
};
